import { FC } from 'react';
import { ERROR_CODES } from '@shinetools/errors';

import { getServerErrorCode } from 'common/errors/utils';
import Button from 'components/_core/Button';
import Modal from 'components/_core/Modal';
import useToast from 'components/ToastProvider/useToast';
import { InvitationCostReview } from 'features/Team/components/InvitationCostReview';

import { useUnlockUser } from '../../../../hooks/useUnlockUser';
import locales from './locales';

interface UnlockTeamMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onInvitationQuotaError: () => void;
  onUnlockUser: () => void;
  user: {
    companyUserOrInvitationId: string;
    firstName: string;
    lastName: string;
  };
}

const UnlockTeamMemberModal: FC<UnlockTeamMemberModalProps> = ({
  isOpen,
  onClose,
  onInvitationQuotaError,
  onUnlockUser,
  user: { companyUserOrInvitationId, firstName, lastName },
}) => {
  const { showToast } = useToast();

  const [unlockCompanyUser, { loading }] = useUnlockUser(
    companyUserOrInvitationId,
  );

  const onSubmit = async () => {
    try {
      await unlockCompanyUser();
      showToast({
        message: locales.formatString(locales.success, {
          firstName,
          lastName,
        }) as string,
        type: 'success',
      });
      onUnlockUser();
    } catch (error) {
      const graphQlErrorCode = getServerErrorCode(error);

      if (
        graphQlErrorCode === ERROR_CODES.COMPANY_INVITATION_QUOTA_LIMIT_REACHED
      ) {
        onInvitationQuotaError();
        return;
      }

      showToast({
        message: locales.errors.invitationError,
        type: 'error',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <Modal.Header />
      <Modal.Body>
        <Modal.Title>
          {locales.formatString(locales.title, {
            firstName,
            lastName,
          })}
        </Modal.Title>

        <Modal.Description paddingBottom="space-32">
          {locales.description}
        </Modal.Description>

        <InvitationCostReview />
      </Modal.Body>

      <Modal.Footer justifyContent="space-between">
        <Button isDisabled={loading} onClick={onClose} variant="inline-primary">
          {locales.cancel}
        </Button>

        <Button icon="send" isLoading={loading} onClick={onSubmit}>
          {locales.ctaLabel}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default UnlockTeamMemberModal;
